import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button as AriaButton, MenuItem as AriaMenuItem } from 'react-aria-components'
import {
  ArrowDownWideNarrow,
  Check,
  ChevronDown,
  Crown,
  Info,
  Languages,
  Play,
  Square,
  Star,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/base/buttons'
import { Dropdown } from '@/components/base/dropdown/dropdown'
import { Badge } from '@/components/ui/Badge'
import { LoadingSpinner, ScoreBar } from '@/components/common'
import { useVoices } from '@/hooks/api'
import { useAudioPreview } from '@/hooks/useAudioPreview'
import { getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'
import { getProviderLabel, translateMetaValue } from '@/lib/voiceDisplay'
import { cn } from '@/lib/utils'
import type { Voice } from '@/types/assistant'
import { PremiumRequestModal } from './PremiumRequestModal'
import {
  assistantLanguageSet,
  isInternalLocked,
  isPremiumLocked,
  isPremiumVoice,
  nativeStatus,
  type NativeStatus,
  voiceCoversLanguages,
} from './voiceEligibility'

function getVoiceScores(voice: Voice): {
  naturalness: number | undefined
  speed: number | undefined
} {
  const stored = voice.meta?.scores
  return {
    naturalness: stored?.naturalness,
    speed: stored?.speed,
  }
}

function isRealtimeVoice(voice: Voice): boolean {
  return voice.provider?.provider_type?.toLowerCase() === 'gemini'
}

type SortKey = 'recommended' | 'natural' | 'fast' | 'popular' | 'name_asc'

// Deterministic gradient palette for voice avatars. Picked by voice.id so each
// voice keeps the same gradient across sessions.
const AVATAR_GRADIENTS: Array<[string, string]> = [
  ['#368f8b', '#5fb3a3'],
  ['#f59e0b', '#fbbf24'],
  ['#8b5cf6', '#a78bfa'],
  ['#06b6d4', '#22d3ee'],
  ['#ec4899', '#f472b6'],
  ['#10b981', '#34d399'],
  ['#f43f5e', '#fb7185'],
  ['#6366f1', '#818cf8'],
]

interface VoiceAvatarProps {
  voice: Voice
  isPlaying: boolean
  isLoading?: boolean
  onPlay: (e: React.MouseEvent) => void
  size?: 'sm' | 'md' | 'lg'
  playPreviewLabel: string
  playingPreviewLabel: string
}

// Exported for reuse by SelectedVoiceCard. Hover reveal is driven by a
// `group/tile` class on an ancestor, which the consumer must provide.
export function VoiceAvatar({
  voice,
  isPlaying,
  isLoading = false,
  onPlay,
  size = 'md',
  playPreviewLabel,
  playingPreviewLabel,
}: VoiceAvatarProps) {
  const [c1, c2] = AVATAR_GRADIENTS[(voice.id ?? 0) % AVATAR_GRADIENTS.length]
  const initial = voice.display_name?.trim()?.[0]?.toUpperCase() ?? '?'
  const dims =
    size === 'lg'
      ? 'h-16 w-16 text-2xl'
      : size === 'sm'
        ? 'h-10 w-10 text-base'
        : 'h-12 w-12 text-lg'
  return (
    <button
      type="button"
      onClick={onPlay}
      aria-label={isPlaying ? playingPreviewLabel : playPreviewLabel}
      className={cn(
        'group relative shrink-0 overflow-hidden rounded-full font-semibold text-white shadow-sm transition-transform hover:scale-105',
        dims
      )}
      style={{ backgroundImage: `linear-gradient(135deg, ${c1}, ${c2})` }}
    >
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center transition-opacity',
          // Touch devices: hide letter so the always-visible play icon stands alone.
          'opacity-0',
          // Hover-capable devices: show letter at rest, hide on tile hover or playback.
          '[@media(hover:hover)]:opacity-100',
          '[@media(hover:hover)]:group-hover/tile:opacity-0',
          isPlaying && '[@media(hover:hover)]:opacity-0'
        )}
      >
        {initial}
      </span>
      <span
        className={cn(
          'absolute inset-0 flex items-center justify-center text-white transition-all',
          isPlaying
            ? 'bg-black/35 opacity-100'
            : cn(
                // Touch devices (no hover): always show the play affordance.
                'bg-black/35 opacity-100',
                // Hover-capable devices: hidden by default, revealed on tile hover.
                '[@media(hover:hover)]:bg-transparent [@media(hover:hover)]:opacity-0',
                '[@media(hover:hover)]:group-hover/tile:bg-black/35 [@media(hover:hover)]:group-hover/tile:opacity-100'
              )
        )}
      >
        {isPlaying ? (
          isLoading ? (
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
              aria-hidden="true"
            />
          ) : (
            <Square className="h-4 w-4 fill-current drop-shadow" />
          )
        ) : (
          <Play className="ml-0.5 h-4 w-4 fill-current drop-shadow" />
        )}
      </span>
    </button>
  )
}

// ElevenLabs-style toolbar pill: bordered chip that highlights when active.
const pillClass = (active: boolean) =>
  cn(
    'inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border px-3 text-sm font-medium transition-colors',
    active
      ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
  )

interface MenuOption {
  value: string
  label: string
}

// The base Dropdown popover uses Untitled UI theme tokens (cream background,
// token ring) that clash with this app's plain gray palette — override with
// the same styling the rest of the app uses for menus (cf. UserMenu).
const menuPopoverClass =
  'rounded-lg border border-gray-200 bg-white shadow-lg ring-0 dark:border-gray-700 dark:bg-gray-900'

function CheckMenuItem({ id, label }: { id: string; label: string }) {
  return (
    <AriaMenuItem
      id={id}
      textValue={label}
      className="group block cursor-pointer px-1.5 py-px outline-none"
    >
      {({ isFocused, isSelected }) => (
        <div
          className={cn(
            'flex items-center justify-between gap-3 rounded-md px-2.5 py-2 transition duration-100 ease-linear',
            isFocused && 'bg-gray-50 dark:bg-gray-800'
          )}
        >
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
          {isSelected && (
            <Check className="text-brand-600 dark:text-brand-400 h-4 w-4" aria-hidden="true" />
          )}
        </div>
      )}
    </AriaMenuItem>
  )
}

interface FilterPillMenuProps {
  /** Filter name, shown when nothing is selected */
  label: string
  /** Label for the "no filter" option */
  anyLabel: string
  options: MenuOption[]
  value: string | null
  onChange: (value: string | null) => void
}

// Filter pill that opens a menu with a check on the current value; shows the
// active choice in the pill itself when a filter is applied.
function FilterPillMenu({ label, anyLabel, options, value, onChange }: FilterPillMenuProps) {
  const selectedOption = options.find((o) => o.value === value)
  return (
    <Dropdown.Root>
      <AriaButton
        aria-label={selectedOption ? `${label}: ${selectedOption.label}` : label}
        className={pillClass(value !== null)}
      >
        {selectedOption ? selectedOption.label : label}
        <ChevronDown className="h-4 w-4 opacity-60" aria-hidden="true" />
      </AriaButton>
      <Dropdown.Popover placement="bottom left" className={cn('w-48', menuPopoverClass)}>
        <Dropdown.Menu
          selectedKeys={[value ?? 'all']}
          onSelectionChange={(keys) => {
            const key = String(Array.from(keys)[0] ?? 'all')
            onChange(key === 'all' ? null : key)
          }}
        >
          <CheckMenuItem id="all" label={anyLabel} />
          {options.map((o) => (
            <CheckMenuItem key={o.value} id={o.value} label={o.label} />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

interface SortMenuButtonProps {
  label: string
  options: MenuOption[]
  value: string
  onChange: (value: string) => void
}

// Icon-only sort trigger; the options only appear in the menu on click.
function SortMenuButton({ label, options, value, onChange }: SortMenuButtonProps) {
  const current = options.find((o) => o.value === value)
  return (
    <Dropdown.Root>
      <AriaButton
        aria-label={current ? `${label}: ${current.label}` : label}
        className={cn(pillClass(value !== 'recommended'), 'px-2.5')}
      >
        <ArrowDownWideNarrow className="h-4 w-4" aria-hidden="true" />
      </AriaButton>
      <Dropdown.Popover placement="bottom right" className={cn('w-52', menuPopoverClass)}>
        <Dropdown.Menu
          selectedKeys={[value]}
          onSelectionChange={(keys) => onChange(String(Array.from(keys)[0] ?? 'recommended'))}
        >
          {options.map((o) => (
            <CheckMenuItem key={o.value} id={o.value} label={o.label} />
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown.Root>
  )
}

interface VoiceCardProps {
  voice: Voice
  isSelected: boolean
  isPlaying: boolean
  isLoadingPreview: boolean
  isRealtime: boolean
  scores: { naturalness: number | undefined; speed: number | undefined }
  onPlay: (e: React.MouseEvent) => void
  onSelect: () => void
  onRequest: () => void
  isRequested: boolean
  canRequest: boolean
  /** Curated native status for the assistant's default language: badge, accent hint, or nothing. */
  nativeStatus: NativeStatus
  /** Display name of the assistant's default language, for the accent hint. */
  assistantLanguageName: string
  translations: {
    premiumPlan: string
    selected: string
    select: string
    request: string
    requested: string
    requestAfterCreate: string
    realtime: string
    recommended: string
    naturalness: string
    responseSpeed: string
    notScored: string
  }
}

function VoiceCard({
  voice,
  isSelected,
  isPlaying,
  isLoadingPreview,
  isRealtime,
  scores,
  onPlay,
  onSelect,
  onRequest,
  isRequested,
  canRequest,
  nativeStatus: native,
  assistantLanguageName,
  translations,
}: VoiceCardProps) {
  const { t, i18n } = useTranslation()
  const flags = (voice.meta?.languages ?? []).slice(0, 3)
  const extraLangs = Math.max(0, (voice.meta?.languages?.length ?? 0) - flags.length)
  const languageNames = (voice.meta?.languages ?? [])
    .map((lang) => getLanguageDisplayName(lang, i18n.language) ?? lang)
    .join(', ')
  const isPremium = isPremiumVoice(voice)
  const premiumLocked = isPremiumLocked(voice)
  const internalLocked = isInternalLocked(voice)
  const voiceName = voice.display_name ?? ''
  const bylineParts = [
    voice.meta?.gender && translateMetaValue(t, 'gender', voice.meta.gender),
    voice.meta?.age_group && translateMetaValue(t, 'age_group', voice.meta.age_group),
    voice.meta?.tones?.[0] && translateMetaValue(t, 'tone', voice.meta.tones[0]),
    getProviderLabel(voice),
  ].filter(Boolean) as string[]

  const metaLine = bylineParts.join(' · ')

  return (
    <div
      className={cn(
        'group/tile flex items-center gap-4 px-5 py-3.5 transition-colors',
        isSelected
          ? 'bg-brand-50/50 dark:bg-brand-900/15'
          : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
      )}
    >
      <VoiceAvatar
        voice={voice}
        size="sm"
        isPlaying={isPlaying}
        isLoading={isLoadingPreview}
        onPlay={onPlay}
        playPreviewLabel={t('resources.assistants.voice_drawer.play_preview_named', {
          name: voiceName,
          defaultValue: 'Play preview of {{name}}',
        })}
        playingPreviewLabel={t('resources.assistants.voice_drawer.stop_preview_named', {
          name: voiceName,
        })}
      />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {voice.display_name}
          </span>
          {voice.meta?.suggested === true && (
            // Subtle recommended marker: a filled star, like browser favorites.
            <span role="img" title={translations.recommended} aria-label={translations.recommended}>
              <Star
                className="text-brand-500 dark:text-brand-400 h-3.5 w-3.5"
                fill="currentColor"
                aria-hidden="true"
              />
            </span>
          )}
          {flags.length > 0 && (
            <span
              role="img"
              title={languageNames}
              aria-label={languageNames}
              className="flex items-center gap-0.5 text-sm leading-none"
            >
              {flags.map((lang) => (
                <span key={lang} aria-hidden="true">
                  {getLanguageFlag(lang)}
                </span>
              ))}
              {extraLangs > 0 && (
                <span className="ml-0.5 text-xs text-gray-400" aria-hidden="true">
                  +{extraLangs}
                </span>
              )}
            </span>
          )}
          {native === 'native' && (
            <Badge variant="success" size="sm">
              {t('resources.assistants.voice_drawer.native')}
            </Badge>
          )}
          {isRealtime && (
            <Badge variant="warning" size="sm" className="gap-0.5">
              <Zap className="h-3 w-3" />
              {translations.realtime}
            </Badge>
          )}
          {isPremium && (
            <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
              <Crown className="h-3 w-3" aria-hidden="true" />
              {translations.premiumPlan}
            </span>
          )}
        </div>

        {(metaLine || voice.description) && (
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {metaLine}
            {voice.description && (
              <span className="italic">
                {metaLine ? ' — ' : ''}
                {voice.description}
              </span>
            )}
          </p>
        )}
        {native === 'accent' && (
          <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
            {t('resources.assistants.voice_drawer.speaks_with_accent', {
              language: assistantLanguageName,
            })}
          </p>
        )}
      </div>

      {/* Fixed column widths (w-28/w-32) keep the score bars and buttons
          aligned across rows and match the column header above the list. */}
      <div className="hidden shrink-0 items-center gap-4 xl:flex">
        <div className="w-28" title={translations.naturalness}>
          <ScoreBar
            score={scores.naturalness}
            label={translations.naturalness}
            notScoredLabel={translations.notScored}
          />
        </div>
        <div className="w-28" title={translations.responseSpeed}>
          <ScoreBar
            score={scores.speed}
            label={translations.responseSpeed}
            notScoredLabel={translations.notScored}
          />
        </div>
      </div>

      {premiumLocked ? (
        canRequest ? (
          <Button
            type="button"
            color="secondary"
            size="sm"
            onPress={onRequest}
            isDisabled={isRequested}
            aria-label={t('resources.assistants.voice_drawer.request_voice_named', {
              name: voiceName,
            })}
            className="w-32 shrink-0"
          >
            {isRequested ? translations.requested : translations.request}
          </Button>
        ) : (
          // No assistant yet (create/clone): the request needs a subscription
          // to attach to, so say so instead of showing a dead button.
          <span className="w-32 shrink-0 text-center text-[11px] leading-tight text-gray-500 dark:text-gray-400">
            {translations.requestAfterCreate}
          </span>
        )
      ) : (
        <Button
          type="button"
          color={isSelected ? 'secondary' : 'primary'}
          size="sm"
          onPress={onSelect}
          isDisabled={internalLocked}
          iconLeading={isSelected ? Check : undefined}
          aria-label={t('resources.assistants.voice_drawer.select_voice_named', {
            name: voiceName,
          })}
          className="w-32 shrink-0"
        >
          {isSelected ? translations.selected : translations.select}
        </Button>
      )}
    </div>
  )
}

export function VoicePicker({
  selectedVoiceId,
  onSelect,
  greeting,
  defaultLanguage = 'en-US',
  additionalLanguages,
  assistantId,
}: VoicePickerProps) {
  const { t, i18n } = useTranslation()
  const {
    playingId: playingVoiceId,
    loadingId: loadingVoiceId,
    toggle: togglePreview,
  } = useAudioPreview({
    errorMessage: t('resources.assistants.voice_drawer.preview_failed', {
      defaultValue: 'Could not play the voice preview.',
    }),
  })

  const [filters, setFilters] = useState({
    age_group: null as string | null,
    gender: null as string | null,
  })
  const [sortKey, setSortKey] = useState<SortKey>('recommended')

  const {
    data: voices = [],
    isLoading,
    isError,
    refetch,
  } = useVoices({
    assistant_id: assistantId,
  })

  const filterOptions = useMemo(() => {
    const ageGroups = new Set<string>()
    const genders = new Set<string>()
    voices.forEach((voice) => {
      if (voice.meta?.age_group) ageGroups.add(voice.meta.age_group)
      if (voice.meta?.gender) genders.add(voice.meta.gender)
    })
    return {
      age_group: Array.from(ageGroups).sort(),
      gender: Array.from(genders).sort(),
    }
  }, [voices])

  // The full set of languages this assistant is configured to handle.
  const assistantLanguages = useMemo(
    () => assistantLanguageSet(defaultLanguage, additionalLanguages),
    [defaultLanguage, additionalLanguages]
  )

  // Voices that declare every assistant language are the sensible default, but
  // the gate is a visible, switchable pill so the hidden voices stay reachable
  // (a voice covering only the primary language is still a valid choice).
  const [requireAllLanguages, setRequireAllLanguages] = useState(true)
  const coveringVoices = useMemo(
    () => voices.filter((v) => voiceCoversLanguages(v, assistantLanguages)),
    [voices, assistantLanguages]
  )
  const hiddenByLanguageCount = voices.length - coveringVoices.length
  const eligibleVoices = requireAllLanguages ? coveringVoices : voices

  // Count of recommended voices in the eligible set, used to gate the toggle.
  const recommendedCount = useMemo(
    () => eligibleVoices.filter((v) => v.meta?.suggested === true).length,
    [eligibleVoices]
  )

  // View mode is derived: the default follows the loaded data until the user
  // explicitly picks a view. The current selection is always visible in the
  // SelectedVoiceCard next to the list, so it doesn't influence the default.
  const [userViewMode, setUserViewMode] = useState<'recommended' | 'all' | null>(null)
  const computedDefaultViewMode = useMemo<'recommended' | 'all'>(
    () => (recommendedCount === 0 ? 'all' : 'recommended'),
    [recommendedCount]
  )
  const viewMode = userViewMode ?? computedDefaultViewMode

  // The selection is NOT excluded here: it shows highlighted in place within
  // its section, while SelectedVoiceCard keeps it visible when filtered out.
  const filteredVoices = useMemo(() => {
    return eligibleVoices.filter((voice) => {
      if (viewMode === 'recommended' && voice.meta?.suggested !== true) return false
      if (filters.age_group && voice.meta?.age_group !== filters.age_group) return false
      if (filters.gender && voice.meta?.gender !== filters.gender) return false
      return true
    })
  }, [eligibleVoices, filters, viewMode])

  // The 0-100 usage score floors to 0 for most voices; the raw assistant
  // count keeps the ordering meaningful when the backend provides it.
  const popularity = (voice: Voice) => voice.assistant_count ?? voice.usage ?? 0

  const sortedVoices = useMemo(() => {
    const list = [...filteredVoices]
    switch (sortKey) {
      case 'natural':
        list.sort(
          (a, b) => (getVoiceScores(b).naturalness ?? 0) - (getVoiceScores(a).naturalness ?? 0)
        )
        break
      case 'fast':
        list.sort((a, b) => (getVoiceScores(b).speed ?? 0) - (getVoiceScores(a).speed ?? 0))
        break
      case 'popular':
        list.sort((a, b) => popularity(b) - popularity(a))
        break
      case 'name_asc':
        list.sort((a, b) => (a.display_name || '').localeCompare(b.display_name || ''))
        break
      case 'recommended':
      default:
        list.sort((a, b) => {
          const aW = a.meta?.suggested_weight ?? 0
          const bW = b.meta?.suggested_weight ?? 0
          if (aW !== bW) return bW - aW
          return popularity(b) - popularity(a)
        })
    }
    return list
  }, [filteredVoices, sortKey])

  const assistantLanguageName =
    getLanguageDisplayName(defaultLanguage, i18n.language) ?? defaultLanguage

  const handlePlayPreview = (voice: Voice, e: React.MouseEvent) => {
    e.stopPropagation()
    void togglePreview(voice.id, {
      text: greeting,
      language: defaultLanguage,
      id: assistantId || 0,
      voice_id: voice.id,
    })
  }

  const [requestTarget, setRequestTarget] = useState<Voice | null>(null)
  const [requestedVoiceIds, setRequestedVoiceIds] = useState<Set<number>>(new Set())

  const voiceCardTranslations = {
    premiumPlan: t('resources.assistants.voice_drawer.premium_plan'),
    selected: t('resources.assistants.voice_drawer.selected'),
    select: t('resources.assistants.voice_drawer.select'),
    request: t('resources.assistants.voice_drawer.premium_request.button'),
    requested: t('resources.assistants.voice_drawer.premium_request.requested'),
    requestAfterCreate: t(
      'resources.assistants.voice_drawer.premium_request.available_after_create'
    ),
    realtime: t('resources.assistants.voice_drawer.realtime'),
    recommended: t('resources.assistants.voice_drawer.recommended_voice', {
      defaultValue: 'Recommended voice',
    }),
    naturalness: t('resources.assistants.voice_drawer.naturalness'),
    responseSpeed: t('resources.assistants.voice_drawer.response_speed'),
    notScored: t('resources.assistants.voice_drawer.not_scored', {
      defaultValue: 'Not scored',
    }),
  }

  const allLabel = t('resources.assistants.voice_drawer.filters.all')

  const sortLabel = t('resources.assistants.voice_drawer.filters.sort')
  const sortOptions = [
    { value: 'recommended', label: t('resources.assistants.voice_drawer.sort.recommended') },
    { value: 'natural', label: t('resources.assistants.voice_drawer.sort.most_natural') },
    { value: 'fast', label: t('resources.assistants.voice_drawer.sort.fastest') },
    { value: 'popular', label: t('resources.assistants.voice_drawer.sort.popular') },
    {
      value: 'name_asc',
      label: t('resources.assistants.voice_drawer.filters.name_asc', { defaultValue: 'Name A-Z' }),
    },
  ]
  const renderVoiceList = (list: Voice[]) =>
    list.map((voice) => (
      <VoiceCard
        key={voice.id}
        voice={voice}
        isSelected={voice.id === selectedVoiceId}
        isPlaying={playingVoiceId === voice.id}
        isLoadingPreview={loadingVoiceId === voice.id}
        isRealtime={isRealtimeVoice(voice)}
        scores={getVoiceScores(voice)}
        onPlay={(e) => handlePlayPreview(voice, e)}
        onSelect={() => onSelect(voice)}
        onRequest={() => setRequestTarget(voice)}
        isRequested={requestedVoiceIds.has(voice.id)}
        canRequest={Boolean(assistantId)}
        nativeStatus={nativeStatus(voice, defaultLanguage)}
        assistantLanguageName={assistantLanguageName}
        translations={voiceCardTranslations}
      />
    ))

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
      {/* Header. The current selection is shown in detail by SelectedVoiceCard
          in the adjacent column. */}
      <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-700">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          {t('resources.assistants.voice_drawer.title')}
        </h3>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.voice_drawer.subtitle')}
        </p>
      </div>

      {/* Toolbar: recommended toggle + filter pills on the left, the sort
          icon (menu on click) at the right, above the list */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
        <button
          type="button"
          onClick={() => setUserViewMode(viewMode === 'recommended' ? 'all' : 'recommended')}
          disabled={recommendedCount === 0}
          aria-pressed={viewMode === 'recommended'}
          className={cn(
            pillClass(viewMode === 'recommended'),
            'disabled:cursor-not-allowed disabled:opacity-50'
          )}
        >
          <Star
            className="h-3.5 w-3.5"
            fill={viewMode === 'recommended' ? 'currentColor' : 'none'}
            aria-hidden="true"
          />
          {t('resources.assistants.voice_drawer.view_recommended')}
        </button>
        <button
          type="button"
          onClick={() => setRequireAllLanguages((v) => !v)}
          aria-pressed={requireAllLanguages}
          className={pillClass(requireAllLanguages)}
        >
          <Languages className="h-3.5 w-3.5" aria-hidden="true" />
          {t('resources.assistants.voice_drawer.filters.supports_languages')}
          {requireAllLanguages && hiddenByLanguageCount > 0 && (
            <span className="text-xs opacity-70">
              {t('resources.assistants.voice_drawer.filters.hidden_count', {
                count: hiddenByLanguageCount,
              })}
            </span>
          )}
        </button>
        {filterOptions.gender.length > 0 && (
          <FilterPillMenu
            label={t('resources.assistants.voice_drawer.filters.gender')}
            anyLabel={allLabel}
            options={filterOptions.gender.map((value) => ({
              value,
              label: translateMetaValue(t, 'gender', value),
            }))}
            value={filters.gender}
            onChange={(v) => setFilters((f) => ({ ...f, gender: v }))}
          />
        )}
        {filterOptions.age_group.length > 0 && (
          <FilterPillMenu
            label={t('resources.assistants.voice_drawer.filters.age_group')}
            anyLabel={allLabel}
            options={filterOptions.age_group.map((value) => ({
              value,
              label: translateMetaValue(t, 'age_group', value),
            }))}
            value={filters.age_group}
            onChange={(v) => setFilters((f) => ({ ...f, age_group: v }))}
          />
        )}
        <div className="ml-auto">
          <SortMenuButton
            label={sortLabel}
            options={sortOptions}
            value={sortKey}
            onChange={(v) => setSortKey(v as SortKey)}
          />
        </div>
      </div>

      {/* Body: full-bleed rows separated by hairlines instead of nested cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <div className="mx-auto max-w-md px-5 py-12 text-center">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.assistants.voice_drawer.load_failed', {
              defaultValue: 'Failed to load voices.',
            })}
          </h3>
          <Button type="button" color="secondary" onPress={() => refetch()} className="mt-4">
            {t('resources.assistants.voice_drawer.retry', { defaultValue: 'Retry' })}
          </Button>
        </div>
      ) : eligibleVoices.length === 0 ? (
        <div className="mx-auto max-w-md px-5 py-12 text-center">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {t('resources.assistants.voice_drawer.no_voices_for_language_set')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('resources.assistants.voice_drawer.no_voices_for_language_set_description', {
              languages: assistantLanguages
                .map((l) => getLanguageDisplayName(l, i18n.language) ?? l)
                .join(', '),
            })}
          </p>
        </div>
      ) : sortedVoices.length === 0 ? (
        <div className="px-5 py-12 text-center text-gray-500">
          {t('resources.assistants.voice_drawer.no_voices_found')}
        </div>
      ) : (
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {/* Column header explaining the score columns; mirrors the row
              layout (flex-1 name area, w-28 score columns, w-32 button) and
              is only shown when the score columns themselves are (xl+) */}
          <div className="hidden items-center gap-4 px-5 py-2 xl:flex">
            <div className="min-w-0 flex-1" />
            <div className="flex shrink-0 items-center gap-4">
              <span
                className="flex w-28 cursor-help items-center gap-1 text-[11px] font-semibold tracking-wider whitespace-nowrap text-gray-400 uppercase dark:text-gray-500"
                title={t('resources.assistants.voice_drawer.naturalness_description', {
                  defaultValue: 'How human and lifelike the voice sounds',
                })}
              >
                {voiceCardTranslations.naturalness}
                <Info className="h-3 w-3" aria-hidden="true" />
              </span>
              <span
                className="flex w-28 cursor-help items-center gap-1 text-[11px] font-semibold tracking-wider whitespace-nowrap text-gray-400 uppercase dark:text-gray-500"
                title={t('resources.assistants.voice_drawer.response_speed_description', {
                  defaultValue: 'How quickly the assistant starts speaking during a call',
                })}
              >
                {voiceCardTranslations.responseSpeed}
                <Info className="h-3 w-3" aria-hidden="true" />
              </span>
            </div>
            <div className="w-32 shrink-0" aria-hidden="true" />
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {renderVoiceList(sortedVoices)}
          </div>
        </div>
      )}

      {assistantId ? (
        <PremiumRequestModal
          voice={requestTarget}
          assistantId={assistantId}
          onClose={() => setRequestTarget(null)}
          onRequested={(voice) => setRequestedVoiceIds((ids) => new Set(ids).add(voice.id))}
        />
      ) : null}
    </div>
  )
}

interface VoicePickerProps {
  selectedVoiceId?: number | null
  onSelect: (voice: Voice) => void
  greeting?: string
  defaultLanguage?: string
  additionalLanguages?: string[]
  assistantId?: number
}

export default VoicePicker
